import type { DesktopGridItem } from 'shared/types/desktop-grid'

import type { BaseLayout, LayoutMode } from './types'

const baseLayoutStores: Record<LayoutMode, Map<string, BaseLayout>> = {
  desktop: new Map(),
  sequential: new Map()
}

let activeMode: LayoutMode = 'desktop'

export const setLayoutMode = (mode: LayoutMode) => {
  activeMode = mode
}

export const getLayoutMode = (): LayoutMode => activeMode

const getStore = () => baseLayoutStores[activeMode]

export const clearLayoutMemory = () => {
  baseLayoutStores.desktop.clear()
  baseLayoutStores.sequential.clear()
}

export const clearNonProfileMemory = () => {
  // No-op: kept for API compatibility.
}

/** Drop saved base-layout anchors so re-inserted items don't inherit old grid slots. */
export const forgetBaseLayoutEntriesForItemIds = (itemIds: string[]) => {
  for (const id of itemIds) {
    baseLayoutStores.desktop.delete(id)
    baseLayoutStores.sequential.delete(id)
  }
}

export const ensureBaseLayout = (
  items: DesktopGridItem[],
  cols: number,
  rows: number
) => {
  if (cols <= 0 || rows <= 0) return
  const store = getStore()
  items.forEach(item => {
    if (store.has(item.id)) return
    store.set(item.id, {
      position: { ...item.position },
      size: { ...item.size },
      grid: { cols },
      order: store.size
    })
  })
}

export const updateBaseLayout = (
  items: DesktopGridItem[],
  cols: number,
  rows: number
) => {
  if (cols <= 0 || rows <= 0) return

  const store = getStore()

  const sortedByPosition = [...items].sort((a, b) => {
    if (a.position.row !== b.position.row)
      return a.position.row - b.position.row
    return a.position.col - b.position.col
  })

  const orderMap = new Map<string, number>()
  sortedByPosition.forEach((item, index) => {
    orderMap.set(item.id, index)
  })

  const activeIds = new Set<string>()
  items.forEach(item => {
    store.set(item.id, {
      position: { ...item.position },
      size: { ...item.size },
      grid: { cols },
      order: orderMap.get(item.id) ?? 0
    })
    activeIds.add(item.id)
  })

  Array.from(store.keys()).forEach(id => {
    if (!activeIds.has(id)) {
      store.delete(id)
    }
  })
}

export const getBaseLayout = (itemId: string): BaseLayout | undefined =>
  getStore().get(itemId)
